import React, { useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { Form } from 'antd';

import { produce } from 'immer';

import { cloneDeep, set } from 'lodash';

import { useProductHierarchies } from 'hooks';
import { useGetProductVersionList } from 'pages/product-history/hook';

import { CustomNotification, StyledModal } from 'common/components';

import AddProductHierarchy from 'pages/home/ribbon/components/modal/product-hierarchy/AddProductHierarchy';

import HierarchyLevelList from 'pages/home/ribbon/components/modal/product-hierarchy/HierarchyLevelList';

import {
  IXONE_HIERARCHY_LEVEL,
  IXONE_INIT_ADDING_HIERARCHY_LEVELS,
  modalIXOneCaptureWidth,
} from './constants';

import * as utils from 'utils/productHierarchy';
import Messages from 'i18n/messages/product';

import 'pages/home/ribbon/components/modal/product-hierarchy/AddHierarchyModal.less';

export const IXOneHierarchyAddModal = (props) => {
  const intl = useIntl();
  const hierarchyProductListRef = useRef([]);
  const [formInstance] = Form.useForm();

  const {
    visible,
    setVisible,
    selectedHierarchy,
    productId,
    currentProduct,
    onCallApiSuccess = () => {},
  } = props;

  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [hierarchyLevelList, setHierarchyLevelList] = useState(
    cloneDeep(IXONE_INIT_ADDING_HIERARCHY_LEVELS)
  );

  const { handleRefetchProductVersioning } = useGetProductVersionList({
    enabled: false,
  });

  //* refactor hierarchyProductList from state to ref
  const hierarchyProductList = hierarchyProductListRef.current;
  const setHierarchyProductList = (newState) => {
    const data =
      typeof newState === 'function'
        ? newState(hierarchyProductList)
        : newState;

    hierarchyProductListRef.current = data;
  };

  const { saveHierarchiesMutation } = useProductHierarchies({
    productId,
  });

  const onCloseModal = () => {
    setVisible(false);
  };

  const onOkHandler = () => {
    if (selectedLevel) {
      selectProductForLevel();
      return;
    }

    callApi();
  };

  const selectProductForLevel = () => {
    if (!selectedLevel) return;
    appendSelectedProductToProductList();

    setHierarchyLevelList((prevState) => {
      const cloneState = cloneDeep(prevState);
      //* set product to level

      set(
        cloneState,
        `[${selectedLevel.index}].productItemId`,
        selectedProduct?.id
      );

      utils.setParentProductForThisLevel({
        levelList: cloneState,
        level: selectedLevel,
        addedProductId: selectedProduct?.id,
      });

      utils.setParentProductForChildrenLevel({
        levelList: cloneState,
        productList: hierarchyProductList,
        level: selectedLevel,
        addedProductId: selectedProduct?.id,
      });

      updateQuantityForm({ levelList: cloneState });

      return cloneState;
    });

    goBackToSelectLevelView();
  };

  const appendSelectedProductToProductList = () => {
    if (!selectedProduct) return;
    setHierarchyProductList((prevState) => {
      const productItem = cloneDeep(selectedProduct);

      set(productItem, 'productItemId', productItem.id);

      const newState = [...prevState, productItem];

      return newState;
    });
  };

  const updateQuantityForm = ({ levelList }) => {
    const hightestLevel = utils.getHighestLevel(levelList);
    const isHighestLevel = selectedLevel.packageLevel === hightestLevel;

    formInstance.setFieldsValue({
      [selectedLevel.index]: isHighestLevel ? 1 : null,
    });
  };

  const getApiParams = async () => {
    try {
      const quantities = await formInstance.validateFields();

      const clonedHierarchyLevelList = cloneDeep(hierarchyLevelList);
      const levelHasProductList = clonedHierarchyLevelList.filter((level) => {
        return level.productItemId;
      });

      const itemsParam = levelHasProductList.map((level) => {
        const { productItemId, parentProductItemId, packageLevel } = level;
        return {
          productItemId,
          parentProductItemId,
          packageLevel,
          quantity: quantities[level.index],
        };
      });

      return {
        hierarchyId: undefined,
        ownerId: currentProduct?.ownerId,
        items: itemsParam,
        isOverlay: currentProduct?.isOverlayProduct,
      };
    } catch (error) {
      console.log('error: ', error);
      return;
    }
  };

  const getApiMessages = () => {
    return {
      success: intl.formatMessage(Messages['addHierarchySuccess']),
      error: intl.formatMessage(Messages['addHierarchyError']),
    };
  };

  const callApi = async () => {
    const params = await getApiParams();
    const messages = getApiMessages();

    saveHierarchiesMutation.mutate(params, {
      onSuccess: (response) => {
        if (!response.isSuccess) {
          CustomNotification.error(response.message || messages.error);
          return;
        }
        handleRefetchProductVersioning();
        CustomNotification.success(messages.success);
        setVisible(false);
        onCallApiSuccess();
      },
      onError: () => {
        CustomNotification.error(messages.error);
      },
    });
  };

  const goBackToSelectLevelView = () => {
    setSelectedLevel(null);
    setSelectedProduct(null);
  };

  const clearState = () => {
    setHierarchyProductList([]);
    setHierarchyLevelList(cloneDeep(IXONE_INIT_ADDING_HIERARCHY_LEVELS));
    setSelectedLevel(null);
    setSelectedProduct(null);
    formInstance.resetFields();
  };

  const updateHierarchyLevelListWithEqualLevel = (levelKey, levelPackage) => {
    setHierarchyLevelList((prevState) => {
      const newState = produce(prevState, (draft) => {
        const equalWithIndex = draft.findIndex((levelItem) =>
          levelItem?.equalWith?.includes(levelKey)
        );

        if (equalWithIndex === -1) return; // no match found, bail out

        draft[equalWithIndex].key = levelKey;
        draft[equalWithIndex].packageLevel = levelPackage;
      });
      return newState;
    });
  };

  const setCurrentProductToHierarchyLevelList = (level) => {
    setHierarchyLevelList((prevState) => {
      const newState = produce(prevState, (draft) => {
        const index = draft.findIndex((levelItem) => levelItem.key === level);

        draft[index].productItemId = currentProduct?.productId;

        //* set parentProductId for first lower level
        if (index < 2)
          draft[index + 1].parentProductItemId = currentProduct?.productId;
      });

      return newState;
    });
  };

  const addCurrentProductToHierarchyProductList = () => {
    const productItem = {
      productItemId: currentProduct.productId,
      productName: currentProduct.productName,
      thumbnail: currentProduct.productPrimaryImage,
      packageLevel: currentProduct.packageLevel,
    };

    setHierarchyProductList((prevState) => {
      prevState.push(productItem);

      return prevState;
    });
  };

  const initCurrentProduct = () => {
    if (!visible) return;

    if (!currentProduct) return;

    const currentProductLevel = currentProduct.packageLevel;

    const level = Object.values(IXONE_HIERARCHY_LEVEL).find(
      (LEVEL) => LEVEL.NAME === currentProductLevel
    )?.KEY;

    const levelIndex = hierarchyLevelList.findIndex(
      (levelItem) => levelItem.key === level
    );

    if (levelIndex === -1) {
      //* levelIndex === -1 when level is not exist in INIT_ADDING_HIERARCHY_LEVELS (such as Display Shipper, ...)
      //* replace equal level with this level
      updateHierarchyLevelListWithEqualLevel(level, currentProductLevel);
    }

    addCurrentProductToHierarchyProductList();
    setCurrentProductToHierarchyLevelList(level);
  };

  const initQuantityForm = ({ levelList }) => {
    const initialQuantityForm = utils.getInitialQuantity({ levelList });

    formInstance.setFieldsValue(initialQuantityForm);
  };

  const checkHighestLevelProduct = () => {
    const highestLevelProduct = utils.getHighestLevelProduct({
      levelList: hierarchyLevelList,
    });

    const currentHighestLevelProduct =
      utils.getSelectedHierarchyHighestLevelProduct({
        levelList: hierarchyLevelList,
        selectedHierarchy,
      });

    return highestLevelProduct === currentHighestLevelProduct;
  };

  const updateSubmitBtnDisabled = () => {
    if (!selectedHierarchy) return false;

    if (selectedLevel) return false;

    let disabled = checkHighestLevelProduct();

    return disabled;
  };

  //* mount update
  useEffect(() => {
    clearState();
    initCurrentProduct();
    //eslint-disable-next-line
  }, [visible]);

  const submitDisabled = updateSubmitBtnDisabled();

  //* render
  const modalTitle = intl.formatMessage(Messages['addHierarchyTitle']);
  const okText = intl.formatMessage(Messages['addHierarchy']);

  const modalProps = {
    title: modalTitle,
    visible,
    onCancel: onCloseModal,
    wrapClassName: 'add-hierarchy__modal',
    bodyStyle: {
      height: selectedLevel ? 405 : 300,
      position: 'relative',
    },
    width: modalIXOneCaptureWidth,
    destroyOnClose: true,
    closeable: false,
    okText,
    onOk: onOkHandler,
    okButtonProps: {
      loading: saveHierarchiesMutation?.isLoading,
      disabled: submitDisabled,
    },
    maskClosable: false,
    zIndex: 1100,
  };

  const addProductHierarchyProps = {
    closeModal: onCloseModal,
    goBackToSelectLevelView,
    selectedLevel,
    selectedProduct,
    setSelectedProduct,
    productId,
  };

  const hierarchyLevelListProps = {
    form: formInstance,
    currentProduct,
    hierarchyLevelList,
    hierarchyProductList,
    selectedHierarchy,
    closeModal: onCloseModal,
    goBackToSelectLevelView,
    setHierarchyLevelList,
    setHierarchyProductList,
    setSelectedLevel,
    checkHighestLevelProduct,
    initQuantityForm,
  };

  return (
    <StyledModal {...modalProps}>
      {selectedLevel ? (
        <AddProductHierarchy {...addProductHierarchyProps} />
      ) : (
        <HierarchyLevelList {...hierarchyLevelListProps} />
      )}
    </StyledModal>
  );
};
